import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import Price from '../components/Price';
import ProductReviews from '../components/ProductReviews';
import { ShoppingBag, ArrowLeft, ShoppingCart, FileIcon, Printer, Truck, ArrowRight, Users, ShieldCheck, Languages } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import ShareButton from '../components/ShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('data:')) return img;
  return `data:image/jpeg;base64,${img}`;
};

export default function PublicProductPage() {
  const { t, i18n } = useTranslation();
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOriginal, setShowOriginal] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = showOriginal ? { lang: '' } : undefined;
      const res = await api.get(`/products/${productId}`, params ? { params } : undefined);
      setProduct(res.data);
    } catch {
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  }, [productId, showOriginal]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <ShoppingBag size={48} className="text-muted-foreground/30" />
        <p className="text-muted-foreground">Product not found</p>
        <Link to="/marketplace"><Button variant="outline" className="rounded-full">Back to Marketplace</Button></Link>
      </div>
    );
  }

  const isPod = product.is_design_product || product.category === 'design';
  const imageSrc = getImageSrc(product.images?.[0]);
  const fileCount = product.file_count || product.download_files?.length || 0;

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: product.description,
    image: imageSrc || undefined,
    url: `${window.location.origin}/products/${product.id}`,
    offers: {
      '@type': 'Offer',
      price: product.price?.toFixed(2),
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Person',
        name: product.seller_name,
      },
    },
    category: product.category,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Marketplace', item: `${window.location.origin}/marketplace` },
      { '@type': 'ListItem', position: 2, name: product.title, item: `${window.location.origin}/products/${product.id}` },
    ],
  };

  const combinedSchema = [productSchema, breadcrumbSchema];

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title={product.title}
        description={product.description?.slice(0, 160)}
        image={imageSrc}
        url={`${window.location.origin}/products/${product.id}`}
        type="product"
        schema={combinedSchema}
      />

      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-4 md:px-8">
          <Link to="/" className="font-serif text-lg tracking-tight">
            <span className="text-obsidian">Push</span>
            <span className="text-amber-600 font-bold">Pro</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/marketplace">
              <Button variant="ghost" className="text-xs h-9 px-4 hidden md:flex">Marketplace</Button>
            </Link>
            <Link to={user ? '/feed' : '/auth'}>
              <Button className="rounded-sm text-xs uppercase tracking-wider h-9 px-6 bg-obsidian hover:bg-amber-600 transition-colors duration-300">
                {user ? 'Dashboard' : 'Get Started'}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-6" data-testid="product-breadcrumb">
          <Link to="/marketplace" className="hover:text-amber-600 transition-colors">Marketplace</Link>
          <span>/</span>
          <span className="text-foreground truncate">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Image Section */}
          <div className="space-y-4">
            <div className="aspect-square bg-stone-50 border border-border/40 overflow-hidden rounded-2xl">
              {imageSrc ? (
                <img src={imageSrc} alt={product.title} className="w-full h-full object-cover" data-testid="public-product-image" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingBag size={48} className="text-muted-foreground/20" />
                </div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.slice(0, 4).map((img, i) => (
                  <div key={i} className="aspect-square bg-stone-50 border border-border/40 overflow-hidden rounded-lg">
                    <img
                      src={img.startsWith('/api/') ? `${BACKEND_URL}${img}` : img}
                      alt={`Preview ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-stone-100 text-foreground rounded-full text-[10px] uppercase">{product.category}</Badge>
              {isPod && (
                <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] rounded-full">
                  <Printer size={10} className="mr-1" /> Print-on-Demand
                </Badge>
              )}
            </div>

            <h1 className="font-serif text-3xl md:text-4xl mb-2 text-obsidian" data-testid="public-product-title">{product.title}</h1>
            {product.subtitle && (
              <p className="text-sm text-amber-700/70 font-light mb-3 italic">{product.subtitle}</p>
            )}

            {/* Auto-translation banner for non-owner visitors when listing source language differs */}
            {(() => {
              const viewerLang = (i18n.language || 'en').split('-')[0];
              const source = (product.source_language || product.original_language || 'en').split('-')[0];
              if (!source || source === viewerLang) return null;
              return (
                <div
                  className="inline-flex items-center gap-2 bg-amber-50/70 border border-amber-200/80 px-2.5 py-1 rounded-full text-[10px] text-amber-700 mb-3"
                  data-testid="public-product-translation-banner"
                >
                  <Languages size={11} />
                  <span>
                    {showOriginal
                      ? (t('product.viewing_original') !== 'product.viewing_original' ? t('product.viewing_original', { lang: source.toUpperCase() }) : `Viewing original (${source.toUpperCase()})`)
                      : (t('product.translated_from') !== 'product.translated_from' ? t('product.translated_from', { lang: source.toUpperCase() }) : `Auto-translated from ${source.toUpperCase()}`)}
                  </span>
                  <button
                    onClick={() => setShowOriginal(v => !v)}
                    data-testid="public-product-translation-toggle"
                    className="underline hover:text-amber-900 font-medium"
                  >
                    {showOriginal
                      ? (t('product.show_translation') !== 'product.show_translation' ? t('product.show_translation') : 'Show translation')
                      : (t('product.show_original') !== 'product.show_original' ? t('product.show_original') : 'Show original')}
                  </button>
                </div>
              );
            })()}

            <p className="text-muted-foreground font-light leading-relaxed mb-4">{product.description}</p>

            {/* Product Specifications */}
            {product.specs && (
              <div className="border border-amber-200/40 bg-amber-50/20 rounded-xl p-4 mb-5" data-testid="public-product-specs">
                <h3 className="text-xs uppercase tracking-wider text-amber-700 mb-3 font-medium">Product Details</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {product.specs.source_resolution && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Source Resolution</p><p className="text-xs font-medium text-obsidian">{product.specs.source_resolution}</p></div>}
                  {product.specs.resolution && !product.specs.source_resolution && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Resolution</p><p className="text-xs font-medium text-obsidian">{product.specs.resolution}</p></div>}
                  {product.specs.format && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Format</p><p className="text-xs font-medium text-obsidian">{product.specs.format}</p></div>}
                  {product.specs.dpi_range && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Print Quality</p><p className="text-xs font-medium text-obsidian">{product.specs.dpi_range}</p></div>}
                  {product.specs.dpi && !product.specs.dpi_range && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Print Quality</p><p className="text-xs font-medium text-obsidian">{product.specs.dpi}</p></div>}
                  {product.specs.wall_sizes_included && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Wall Sizes</p><p className="text-xs font-medium text-obsidian">{product.specs.wall_sizes_included} sizes included</p></div>}
                  {product.specs.coverage_sizes && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Coverage</p><p className="text-xs font-medium text-obsidian">{product.specs.coverage_sizes}</p></div>}
                  {product.specs.suitable_for && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Suitable For</p><p className="text-xs font-medium text-obsidian">{product.specs.suitable_for}</p></div>}
                  {product.specs.tiling_compatibility && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Tiling</p><p className="text-xs font-medium text-obsidian">{product.specs.tiling_compatibility}</p></div>}
                  {product.specs.installation_note && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Installation</p><p className="text-xs font-medium text-obsidian">{product.specs.installation_note}</p></div>}
                  {product.specs.delivery && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Delivery</p><p className="text-xs font-medium text-obsidian">{product.specs.delivery}</p></div>}
                </div>
              </div>
            )}

            {/* Creator Protection Badge */}
            <div className="flex gap-2.5 p-3.5 bg-emerald-50/60 border border-emerald-200/40 rounded-xl mb-6" data-testid="public-protection-badge">
              <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-medium text-emerald-800">Creator Protection Enabled</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  <span className="text-[9px] text-emerald-700/80">Secure File Delivery</span>
                  <span className="text-[9px] text-emerald-700/80">Protected Downloads</span>
                  <span className="text-[9px] text-emerald-700/80">Full Creator Ownership</span>
                </div>
              </div>
            </div>

            {/* Share */}
            <div className="mb-6">
              <ShareButton
                title={product.title}
                url={`${window.location.origin}/products/${product.id}`}
                description={product.description?.slice(0, 140)}
              />
            </div>

            {/* File info for digital */}
            {!isPod && fileCount > 0 && (
              <div className="border border-border/40 rounded-xl p-4 mb-6">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Included Files ({fileCount})</h3>
                <div className="space-y-1.5">
                  {product.download_files?.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <FileIcon size={12} className="text-amber-500" />
                      <span className="truncate">{f.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* POD info */}
            {isPod && (
              <div className="border border-purple-200 bg-purple-50/50 rounded-xl p-4 mb-6">
                <h3 className="text-xs uppercase tracking-wider text-purple-700 mb-1 flex items-center gap-1">
                  <Printer size={12} /> Print-on-Demand Product
                </h3>
                <p className="text-xs text-purple-600/80">Custom printed and shipped to you. Select size & color after purchase.</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Truck size={12} /> Estimated delivery: 5-7 business days
                </p>
              </div>
            )}

            {/* Seller */}
            <div className="flex items-center gap-3 mb-6 py-4 border-y border-border/30">
              <Avatar className="h-10 w-10 border-2 border-amber-100">
                <AvatarFallback className="bg-gradient-to-br from-amber-100 to-rose-100 text-amber-700 font-serif">
                  {(product.seller_name || 'S')[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">{product.seller_name}</p>
                <p className="text-[10px] text-muted-foreground">Creator on PushPro</p>
              </div>
            </div>

            {/* Price + CTA */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-3xl font-serif text-amber-600"><Price amountEur={product.price} /></span>
              <span className="text-xs text-muted-foreground">{product.purchases || 0} purchased</span>
            </div>

            {user ? (
              <Button
                data-testid="public-buy-btn"
                onClick={() => navigate(`/products/${product.id}`)}
                className="w-full rounded-full h-14 bg-obsidian hover:bg-amber-600 transition-colors duration-300 font-medium text-sm"
              >
                <ShoppingCart size={18} className="mr-2" />
                Buy Now {'\u2014'} <Price amountEur={product.price} />
              </Button>
            ) : (
              <div className="space-y-3">
                <Link to="/auth" className="block">
                  <Button
                    data-testid="public-signin-to-buy-btn"
                    className="w-full rounded-full h-14 bg-obsidian hover:bg-amber-600 transition-colors duration-300 font-medium text-sm"
                  >
                    <ShoppingCart size={18} className="mr-2" />
                    Sign In to Buy {'\u2014'} <Price amountEur={product.price} />
                  </Button>
                </Link>
                <p className="text-center text-xs text-muted-foreground">
                  New here? <Link to="/auth" className="text-amber-600 hover:underline">Create a free account</Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Multilingual buyer reviews */}
      <div className="max-w-6xl mx-auto px-4 pb-10">
        <ProductReviews productId={product.id} isOwner={false} purchased={false} />
      </div>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8 bg-white mt-16">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">PushPro. Premium creator marketplace.</p>
          <div className="flex items-center gap-6">
            <Link to="/marketplace" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Marketplace</Link>
            <Link to="/creators" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Creators</Link>
            <Link to="/auth" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
